import React, { useState, useRef, useEffect } from 'react';
import { Image, Play, X, ChevronLeft, ChevronRight, Upload, Loader2, Plus, Trash, Volume2, VolumeX, Star } from 'lucide-react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useTrainerMedia, TrainerMedia } from '@/hooks/useTrainerMedia';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';

interface TrainerMediaGalleryProps {
trainerAuthUid: string | undefined;   // folder name == auth.uid()
  isEditable?: boolean;
}

const TrainerMediaGallery: React.FC<TrainerMediaGalleryProps> = ({ 
trainerAuthUid,
  isEditable = false
}) => {
   if (!trainerAuthUid) return null; 
  const { isDarkMode } = useTheme();
  const { toast } = useToast();
const {
  files,
  upload,
  remove,
  toggleFeatured,
  loading,
  error,
} = useTrainerMedia(trainerAuthUid);
  const [selectedItem, setSelectedItem] = useState<TrainerMedia | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<TrainerMedia | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isModalVideoMuted, setIsModalVideoMuted] = useState(true);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const modalVideoRef = useRef<HTMLVideoElement>(null);
  const gridVideoRefs = useRef<Record<string, HTMLVideoElement>>({});
  const [showAllMedia, setShowAllMedia] = useState(false);
  const modalButtonClasses = cn(
  "absolute p-2 rounded-full transition-colors shadow-lg z-20",
  isDarkMode
    ? "bg-black/70 text-white hover:bg-black/90"
    : "bg-white/70 text-dark-800 hover:bg-white/90"
);
  const [isTogglingFeatured, setIsTogglingFeatured] = useState(false);
  
  // Swipe gesture handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;
  
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    }
    
    if (isRightSwipe) {
      goToPrevious();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Count featured items
  const featuredCount = files.filter(f => f.isFeatured).length;
  
  // Format time in MM:SS format
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Set up 2-second loop for grid videos
  useEffect(() => {
    const videoElements = Object.values(gridVideoRefs.current);
    
    // Set up interval to reset videos to beginning after 2 seconds
    const interval = setInterval(() => {
      videoElements.forEach(video => {
        if (video && !video.paused && video.currentTime > 2) {
          video.currentTime = 0;
        }
      });
    }, 100); // Check frequently to ensure smooth looping
    
    return () => clearInterval(interval);
  }, [files]);

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setIsUploading(true);
    try {
      await upload(e.target.files);
      toast({
        title: "Upload successful",
        description: `${e.target.files.length} file(s) uploaded successfully`,
      });
      // Reset the file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Failed to upload files",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file deletion
  const handleDeleteClick = (file: TrainerMedia, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the modal
    setFileToDelete(file);
    setShowDeleteConfirm(true);
  };
  
  // Confirm deletion
  const confirmDelete = async () => {
    if (!fileToDelete) return;
    setIsDeleting(true);

    try {
      await remove(fileToDelete.path);
      toast({
        title: "File deleted",
        description: "The file has been removed successfully",
      });
      
      // If the deleted file was selected in the modal, close the modal
      if (selectedItem?.id === fileToDelete.id) {
        setSelectedItem(null);
      }
      
      // Close the confirmation dialog
      setShowDeleteConfirm(false);
      setFileToDelete(null);
    } catch (err) {
      toast({
        title: "Deletion failed",
        description: err instanceof Error ? err.message : "Failed to delete file",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle toggling featured status
  const handleToggleFeatured = async (file: TrainerMedia, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the modal
    
    if (isTogglingFeatured) return;
    setIsTogglingFeatured(true);
    
    try {
      await toggleFeatured(file.path, !file.isFeatured);
      toast({
        title: file.isFeatured ? "Removed from featured" : "Added to featured",
        description: file.isFeatured 
          ? "Media removed from featured items" 
          : "Media added to featured items",
      });
    } catch (err) {
      toast({
        title: "Action failed",
        description: err instanceof Error ? err.message : "Failed to update featured status",
        variant: "destructive"
      });
    } finally {
      setIsTogglingFeatured(false);
    }
  };

  // Filter and sort files for display
  const displayFiles = React.useMemo(() => {
    if (showAllMedia) return files;
    
    // First, get featured items
    const featuredFiles = files.filter(file => file.isFeatured);
    
    // If we have 6 or more featured items, just return the first 6
    if (featuredFiles.length >= 6) {
      return featuredFiles.slice(0, 6);
    }
    
    // Otherwise, fill the remaining slots with non-featured items
    const nonFeaturedFiles = files.filter(file => !file.isFeatured);
    return [...featuredFiles, ...nonFeaturedFiles].slice(0, 6);
  }, [files, showAllMedia]);

  // Open modal with selected item
  const openModal = (file: TrainerMedia, index: number) => {
    setSelectedItem(file);
    setIsModalVideoMuted(true); // Reset to muted when opening modal
    setCurrentIndex(index);
  };

  // Navigation in the modal
  const goToPrevious = () => {
    const newIndex = (currentIndex - 1 + files.length) % files.length;
    setCurrentIndex(newIndex);
    setSelectedItem(files[newIndex]);
  };

  const goToNext = () => {
    const newIndex = (currentIndex + 1) % files.length;
    setCurrentIndex(newIndex);
    setSelectedItem(files[newIndex]);
  };

  // Reset video state when modal closes
  useEffect(() => {
    if (!selectedItem) {
      setVideoCurrentTime(0);
      setVideoDuration(0);
      setIsModalVideoMuted(true);
    }
  }, [selectedItem]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedItem) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        setSelectedItem(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItem, currentIndex, files.length]);

  if (loading) {
    return (
      <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
        <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
          <Image className="h-5 w-5 text-gold-400 mr-2 animate-pulse" />
          Training Showcase
        </h2>
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 text-gold-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Image className="h-5 w-5 text-gold-400 mr-2" />
          Training Showcase
        </h2>
        <div className="text-center py-8 bg-dark-300 rounded-xl">
          <p className="text-dark-800">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-dark-400 hover:bg-dark-500 text-white"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-200 rounded-2xl p-6 border border-dark-300">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <Image className="h-5 w-5 text-gold-400 mr-2 group-hover:animate-pulse" />
          Training Showcase
        </h2>
        
        {isEditable && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="bg-gold-400 hover:bg-gold-500 text-dark-50"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Media
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Gallery Grid */}
      {files.length === 0 ? (
        <div className="text-center py-12 bg-dark-300 rounded-xl border border-dark-400">
          <Image className="h-12 w-12 text-dark-800 mx-auto mb-4" />
          <p className="text-dark-800 mb-4">No media has been added yet</p>
          
          {isEditable && (
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-gold-400 hover:bg-gold-500 text-dark-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Media
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-4">
            <AnimatePresence>
              {displayFiles.map((file, index) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={cn(
                    "relative aspect-square rounded-xl overflow-hidden cursor-pointer group",
                    "border border-dark-300 hover:border-gold-400",
                    file.isFeatured && "ring-2 ring-gold-400",
                    "transform transition-all duration-300 hover:scale-[1.03]",
                    "shadow-md hover:shadow-xl"
                  )}
                  onClick={() => openModal(file, files.indexOf(file))}
                >
                  {file.type === 'video' ? (
                    <div className="relative w-full h-full">
                      <video
                        key={file.id}
                        ref={(el) => {
                          if (el) gridVideoRefs.current[file.id] = el;
                        }}
                        src={file.publicUrl}
                        className="w-full h-full object-cover"
                        muted
                        autoPlay
                        loop
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  ) : (
                    <img
                      src={file.publicUrl}
                      alt={file.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                  )}

                  {/* Hover Overlay */}
                  <div className={cn(
                    "absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent",
                    "opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                    "flex flex-col justify-end p-4"
                  )}>
                    <p className="text-white text-sm font-medium drop-shadow-md">
                      {file.name.split('-').slice(1).join('-')}
                    </p>
                  </div>

                  {/* Delete Button (only visible when editable) */}
                  {isEditable && (
                    <button
                      onClick={(e) => handleDeleteClick(file, e)}
                      className={cn(
                        "absolute top-2 right-2 p-2 rounded-full",
                        "bg-red-500/80 text-white",
                        "opacity-0 group-hover:opacity-100 transition-opacity",
                        "hover:bg-red-600 hover:scale-110",
                        "transform transition-all duration-200"
                      )}
                      title="Delete media"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  )}
                  
                  {/* Star/Feature Button (only visible when editable) */}
                  {isEditable && (
                    <button
                      aria-label={file.isFeatured ? "Remove from featured" : "Add to featured"}
                      onClick={(e) => handleToggleFeatured(file, e)}
                      className={cn(
                        "absolute top-2 left-2 p-2 rounded-full",
                        file.isFeatured 
                          ? "bg-gold-400 text-dark-50" 
                          : "bg-dark-400/80 text-white",
                        "opacity-100 transition-opacity",
                        "hover:scale-110",
                        "transform transition-all duration-200"
                      )}
                      title={file.isFeatured 
                        ? "Remove from featured (featured items appear first in your public profile)" 
                        : featuredCount >= 6 
                          ? "Maximum of 6 featured items reached. Unfeature another item first." 
                          : "Add to featured (featured items appear first in your public profile)"}
                      disabled={!file.isFeatured && featuredCount >= 6}
                    >
                      <Star className="h-4 w-4" fill={file.isFeatured ? "currentColor" : "none"} />
                    </button>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Add Media Button (only visible when editable) */}
            {isEditable && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "aspect-square rounded-xl",
                  "border border-dashed border-dark-400",
                  "flex items-center justify-center",
                  "hover:border-gold-400 hover:bg-dark-300/50 transition-all duration-300",
                  "group"
                )}
              >
                <Plus
                  className={cn(
                    "h-8 w-8 text-dark-800",
                    "group-hover:text-gold-400 transition-colors",
                    "transform group-hover:scale-110",
                    "transition-all duration-200"
                  )}
                />
              </button>
            )}
          </div>
          
          {/* Show More/Less Button */}
          {files.length > 6 && (
            <div className="flex justify-center mt-4">
              <Button
                onClick={() => setShowAllMedia(!showAllMedia)}
                variant="outline"
                className="border-dark-400 text-white hover:bg-dark-400"
              >
                {showAllMedia ? "Show Less" : `Show All (${files.length})`}
              </Button>
            </div>
          )}
          
          {/* Featured Items Hint (only visible when editable) */}
          {isEditable && (
            <p className="text-dark-800 text-sm text-center mt-2">
              <span className={featuredCount >= 6 ? "text-gold-400" : ""}>
                {featuredCount}/6 featured items.
              </span>
              {" Featured items appear first in your public profile."}
            </p>
          )}
        </div>
      )}

      {/* Modal for Full-Screen View */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedItem(null)}
          >
            {/* Close Button */}
            <button
              className={cn(modalButtonClasses, "top-4 right-4")}
            
              onClick={() => setSelectedItem(null)}
            >
              <X className="h-6 w-6" />
            </button>

            {/* Navigation Buttons */}
            {files.length > 1 && (
              <>
                <button
                 className={cn(modalButtonClasses, "left-4 top-1/2 transform -translate-y-1/2")}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>

                <button
className={cn(modalButtonClasses, "right-4 top-1/2 transform -translate-y-1/2")}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="relative max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {/* Featured Status in Modal (only visible when editable) */}
              {isEditable && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedItem) {
                      const canFeature = !selectedItem.isFeatured && featuredCount < 6;
                      if (selectedItem.isFeatured || canFeature) {
                        handleToggleFeatured(selectedItem, e);
                      } else {
                        toast({
                          title: "Maximum featured items reached",
                          description: "You can only feature up to 6 items. Unfeature another item first.",
                          variant: "destructive"
                        });
                      }
                    }
                  }}
                  className={cn(
                    "absolute top-4 left-4 p-2 rounded-full z-10",
                    selectedItem?.isFeatured 
                      ? "bg-gold-400 text-dark-50" 
                      : "bg-dark-400/80 text-white",
                    "hover:scale-110 transition-transform"
                  )}
                  disabled={!selectedItem?.isFeatured && featuredCount >= 6}
                >
                  <Star 
                    className="h-6 w-6" 
                    fill={selectedItem?.isFeatured ? "currentColor" : "none"} 
                  />
                </button>
              )}
              
              {selectedItem.type === 'video' ? (
                <div className="relative w-full max-w-4xl aspect-video group">
                  <video
                    ref={modalVideoRef}
                    src={selectedItem.publicUrl}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay={true}
                    muted={isModalVideoMuted}
                    playsInline
                    onTimeUpdate={(e) => setVideoCurrentTime(e.currentTarget.currentTime)}
                    onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
                  />
                  
                  {/* Mute/Unmute Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (modalVideoRef.current) {
                        modalVideoRef.current.muted = !modalVideoRef.current.muted;
                        setIsModalVideoMuted(!isModalVideoMuted);
                      }
                    }}
                    className={cn(
                      "absolute bottom-16 right-4 p-2 rounded-full z-10 shadow-lg",
                      "bg-black/70 text-white hover:bg-black/90 transition-colors", 
                      "opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    )}
                    aria-label={isModalVideoMuted ? "Unmute" : "Mute"}
                  >
                    {isModalVideoMuted ? (
                      <VolumeX className="h-5 w-5" />
                    ) : (
                      <Volume2 className="h-5 w-5" />
                    )}
                  </button>
                  
                  {/* Time Remaining */}
                  {videoDuration > 0 && (
                    <div className={cn(
                      "absolute bottom-16 left-4 px-2 py-1 rounded z-10 shadow-lg",
                      "bg-black/70 text-white text-sm",
                      "opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    )}>
                      {formatTime(videoDuration - videoCurrentTime)}
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src={selectedItem.publicUrl}
                  alt={selectedItem.name}
                  className="max-w-full max-h-full object-contain select-none"
                />
              )}

              {/* Caption */}
              {selectedItem.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                  <p className="text-white text-center">{selectedItem.caption}</p>
                </div>
              )}
            </motion.div>

            {/* Pagination Indicator */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 bg-black/50 px-3 py-1.5 rounded-full">
              {files.map((_, idx) => (
                <button
                  key={idx}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    idx === currentIndex ? "bg-white w-4" : "bg-white/50"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(idx);
                    setSelectedItem(files[idx]);
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[425px] bg-dark-200 border-dark-300 text-white">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this {fileToDelete?.type}?</p>
            <p className="text-dark-800 text-sm mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setFileToDelete(null);
              }}
              className="border-dark-400"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerMediaGallery;